import React from "react";
import { Card, CardContent } from "@/components/ui/card";

interface ProductSegmentCard {
  id: string;
  label: string;
  percent: number;
  baseColor: string;
  stripeColor: string;
}

const BASE_PRODUCT_SEGMENTS: Omit<ProductSegmentCard, "percent">[] = [
  {
    id: "top-revenue",
    label: "Top Revenue SKUs",
    baseColor: "#ecfdf5", // emerald-50
    stripeColor: "rgba(16,185,129,0.18)", // emerald-500
  },
  {
    id: "mid-performers",
    label: "Mid Performers SKUs",
    baseColor: "#eff6ff", // blue-50
    stripeColor: "rgba(59,130,246,0.18)", // blue-500
  },
  {
    id: "low-revenue",
    label: "Low Revenue SKUs",
    baseColor: "#fee2e2", // red-100
    stripeColor: "rgba(248,113,113,0.22)", // red-400
  },
  {
    id: "revenue-growth",
    label: "Revenue Growth SKUs",
    baseColor: "#ecfeff", // cyan-50
    stripeColor: "rgba(34,211,238,0.18)", // cyan-400
  },
  {
    id: "revenue-decline",
    label: "Revenue Decline SKUs",
    baseColor: "#fefce8", // yellow-50
    stripeColor: "rgba(250,204,21,0.22)", // yellow-400
  },
];

const generateRandomProductSegments = (): ProductSegmentCard[] => {
  return BASE_PRODUCT_SEGMENTS.map((segment, index) => {
    let percent: number;

    if (segment.id === "top-revenue") {
      percent = 60 + Math.floor(Math.random() * 31); // 60–90
    } else if (segment.id === "mid-performers") {
      percent = 30 + Math.floor(Math.random() * 31); // 30–60
    } else if (segment.id === "low-revenue") {
      percent = 5 + Math.floor(Math.random() * 21); // 5–25
    } else if (segment.id === "revenue-growth") {
      percent = 10 + Math.floor(Math.random() * 31); // 10–40
    } else {
      // revenue-decline
      percent = 5 + Math.floor(Math.random() * 21); // 5–25
    }

    return {
      ...segment,
      percent,
    };
  });
};

export function SegmentationProduct() {
  const segments = React.useMemo(generateRandomProductSegments, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-800">
          Product Segmentation
        </h2>
        <p className="text-sm text-muted-foreground">
          Contribution of SKUs across revenue and performance segments.
        </p>
      </div>

      <div className="grid gap-4 xl:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {segments.map((segment) => (
          <Card
            key={segment.id}
            className="border-0 shadow-none overflow-hidden"
          >
            <CardContent
              className="p-5 sm:p-6 h-full flex flex-col justify-between"
              style={{
                backgroundColor: segment.baseColor,
                backgroundImage: `repeating-linear-gradient(135deg, ${segment.stripeColor} 0, ${segment.stripeColor} 2px, transparent 2px, transparent 6px)`,
              }}
            >
              <div className="space-y-6 sm:space-y-8">
                <div className="text-sm font-medium text-gray-700">
                  {segment.label}
                </div>

                <div className="space-y-1 sm:space-y-2">
                  <div className="text-4xl sm:text-5xl font-semibold text-gray-900">
                    {segment.percent}
                    <span className="text-2xl align-top ml-1">%</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    of total revenue from this segment
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
